import React, { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';

import { useFetchUserDbqAudit } from '../../../services/userDbq';
import { getDbqAuditRelativeTime } from '../../../lib/dbqAuditTime';

const HIDDEN_ACTIVITY_ACTIONS = new Set(['dbq.suggestion_applied']);

const ACTIVITY_LABELS = Object.freeze({
  'dbq.created': 'Request workspace created',
  'dbq.updated': 'Draft updated',
  'dbq.ai_draft_generated': 'AI draft generated',
  'dbq.ai_draft_updated': 'AI draft updated',
  'dbq.ai_draft_generation_failed': 'AI draft generation failed',
  'dbq.comment_added': 'Comment added',
  'dbq.suggestion_created': 'Suggestion added',
  'dbq.suggestion_accepted': 'Suggestion accepted',
  'dbq.suggestion_rejected': 'Suggestion rejected',
  'dbq.sent': 'Sent to provider',
  'dbq.status_changed': 'Status changed',
  'dbq.pdf_generated': 'PDF generated',
  'dbq.signed': 'DBQ signed',
  'dbq.update_blocked': 'Blocked edit attempt (signed DBQ)',
});

const toRoleLabel = (role) =>
  String(role || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getActivityActorLabel = (event) =>
  event?.actorName || toRoleLabel(event?.eventBody?.role) || 'System';

const getActivityLabel = (event) => {
  // Post-sign edits get their own, louder label than a routine draft save.
  if (event?.eventAction === 'dbq.updated' && event?.eventBody?.postSign) {
    return 'Edited after provider signing';
  }
  return ACTIVITY_LABELS[event?.eventAction] || 'DBQ activity updated';
};

const DbqRequestRecentActivity = ({ userDbqId, enabled = true, limit = 4 }) => {
  const { data, isLoading, isError } = useFetchUserDbqAudit(userDbqId, enabled);

  const activities = useMemo(() => {
    const events = Array.isArray(data?.events) ? data.events : [];
    return events
      .filter((event) => !HIDDEN_ACTIVITY_ACTIONS.has(event?.eventAction))
      .slice(0, limit);
  }, [data?.events, limit]);

  if (!enabled) return null;

  if (isLoading) {
    return (
      <div className="d-flex align-items-center text-muted small">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading recent activity...
      </div>
    );
  }

  if (isError) {
    return <div className="text-muted small">Recent activity is unavailable right now.</div>;
  }

  if (activities.length === 0) {
    return <div className="text-muted small">No recent activity yet.</div>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {activities.map((event) => (
        <div key={event.eventId}>
          <div className="fw-semibold small">{getActivityLabel(event)}</div>
          <div className="text-muted small">
            {getActivityActorLabel(event)} - {getDbqAuditRelativeTime(event.eventDate)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DbqRequestRecentActivity;
